// Import Rive
import * as rive from '@rive-app/webgl2';

// Import Timeline functionality
import { createTimeline, stopTimeline, stopAllTimelines, createTimelineControls, isTimelineActive } from './timeline.js';

// Function to get the Rive file name from URL hash
function getRiveFileFromHash() {
    // Get the hash part of the URL (excluding the # symbol)
    const hash = window.location.hash.substring(1);
    
    // If hash exists and ends with .riv, use it as the source file
    if (hash && hash.endsWith('.riv')) {
        return hash;
    }
    
    // Otherwise, return the default file
    window.location.href = 'list.html';
}

// Configuration
const config = {
    sourceFile: getRiveFileFromHash(),
    fit: rive.Fit.Cover,
    alignment: rive.Alignment.Center
};

// DOM Elements
const elements = {
    canvas: document.getElementById('canvas'),
    watermark: document.getElementById('watermark'),
    bottomSheet: document.getElementById('bottom-sheet'),
    bottomSheetToggle: document.getElementById('bottom-sheet-toggle'),
    bottomSheetClose: document.getElementById('bottom-sheet-close'),
    tabButtons: document.querySelectorAll('.tab-button'),
    tabContents: document.querySelectorAll('.tab-content'),
    controlsContent: document.getElementById('controls-content'),
    dataBindingContent: document.getElementById('data-binding-content'),
    timelineContent: document.getElementById('timeline-content'),
    debugTabButtons: document.querySelectorAll('.debug-tab-button'),
    debugTabContents: document.querySelectorAll('.debug-tab-content'),
    debugAnimationsContent: document.getElementById('debug-tab-animations'),
    debugStateMachinesContent: document.getElementById('debug-tab-statemachines')
};

// Set watermark text to show project title and source file
elements.watermark.textContent = `AniSpark | Source: ${config.sourceFile}`;

// Bottom Sheet Management
function initBottomSheet() {
    // Toggle bottom sheet
    elements.bottomSheetToggle.addEventListener('click', () => {
        elements.bottomSheet.classList.add('open');
        elements.bottomSheetToggle.style.display = 'none'; // Hide toggle when sheet is open
        
        // Transform canvas
        elements.canvas.style.transform = 'translateY(-20vh) scale(0.8)';
        
        // Resize canvas after a short delay to allow CSS transition
        setTimeout(() => {
            if (window.riveInstance) {
                window.riveInstance.resizeDrawingSurfaceToCanvas();
            }
        }, 50);
    });
    
    // Close bottom sheet
    elements.bottomSheetClose.addEventListener('click', () => {
        elements.bottomSheet.classList.remove('open');
        elements.bottomSheetToggle.style.display = 'flex'; // Show toggle when sheet is closed
        
        // Reset canvas transform
        elements.canvas.style.transform = 'translateY(0) scale(1)';
        
        // Resize canvas after a short delay to allow CSS transition
        setTimeout(() => {
            if (window.riveInstance) {
                window.riveInstance.resizeDrawingSurfaceToCanvas();
            }
        }, 50);
    });
    
    // Back button functionality
    const backButton = document.getElementById('back-button');
    if (backButton) {
        backButton.addEventListener('click', () => {
            window.location.href = 'list.html';
        });
    }
    
    // Tab switching
    elements.tabButtons.forEach(button => {
        button.addEventListener('click', () => {
            const targetTab = button.getAttribute('data-tab');
            
            // Update active tab button
            elements.tabButtons.forEach(btn => btn.classList.remove('active'));
            button.classList.add('active');
            
            // Update active tab content
            elements.tabContents.forEach(content => content.classList.remove('active'));
            document.getElementById(`tab-${targetTab}`).classList.add('active');
            
            // Update content based on active tab
            updateTabContent(targetTab);
        });
    });
}

// Debug Panel Management
function initDebugPanel() {
    // Tab switching for debug panel
    elements.debugTabButtons.forEach(button => {
        button.addEventListener('click', () => {
            const targetTab = button.getAttribute('data-debug-tab');

            // Update active tab button
            elements.debugTabButtons.forEach(btn => btn.classList.remove('active'));
            button.classList.add('active');

            // Update active tab content
            elements.debugTabContents.forEach(content => content.classList.remove('active'));
            document.getElementById(`debug-tab-${targetTab}`).classList.add('active');
        });
    });
}

// Update animations list in debug panel
function updateDebugAnimations(instance) {
    if (!instance || !instance.artboard) {
        elements.debugAnimationsContent.innerHTML = '<div class="debug-empty-message">No artboard loaded</div>';
        return;
    }

    const artboard = instance.artboard;

    // animationCount could be a property or a method, handle both cases
    let animationCount = 0;
    if (typeof artboard.animationCount === 'function') {
        animationCount = artboard.animationCount();
    } else {
        animationCount = artboard.animationCount || 0;
    }

    console.log('Animation count:', animationCount);

    if (animationCount === 0) {
        elements.debugAnimationsContent.innerHTML = '<div class="debug-empty-message">No animations found</div>';
        return;
    }

    let html = '';
    for (let i = 0; i < animationCount; i++) {
        const animation = artboard.animationByIndex(i);
        const animName = animation.name;

        console.log(`Animation ${i}: ${animName}`);

        html += `
            <div class="debug-list-item" data-animation-index="${i}" data-animation-name="${animName}">
                <span class="debug-list-item-name">${animName}</span>
            </div>
        `;
    }

    elements.debugAnimationsContent.innerHTML = html;

    // Add click listeners to play animations
    const animationItems = elements.debugAnimationsContent.querySelectorAll('.debug-list-item');
    animationItems.forEach(item => {
        item.addEventListener('click', () => {
            const animName = item.getAttribute('data-animation-name');
            playAnimation(animName);

            // Update active state
            animationItems.forEach(i => i.classList.remove('active'));
            item.classList.add('active');
        });
    });
}

// Update state machines list in debug panel
function updateDebugStateMachines(instance) {
    if (!instance || !instance.artboard) {
        elements.debugStateMachinesContent.innerHTML = '<div class="debug-empty-message">No artboard loaded</div>';
        return;
    }

    const artboard = instance.artboard;

    // stateMachineCount could be a property or a method, handle both cases
    let stateMachineCount = 0;
    if (typeof artboard.stateMachineCount === 'function') {
        stateMachineCount = artboard.stateMachineCount();
    } else {
        stateMachineCount = artboard.stateMachineCount || 0;
    }

    console.log('State machine count:', stateMachineCount);

    if (stateMachineCount === 0) {
        elements.debugStateMachinesContent.innerHTML = '<div class="debug-empty-message">No state machines found</div>';
        return;
    }

    // Build list of state machines
    const stateMachines = [];
    for (let i = 0; i < stateMachineCount; i++) {
        const stateMachine = artboard.stateMachineByIndex(i);
        const smName = stateMachine.name;
        console.log(`State machine ${i}: ${smName}`);
        stateMachines.push({ index: i, name: smName });
    }

    // Store globally
    window.availableStateMachines = stateMachines;

    // Create HTML with select dropdown
    let html = `
        <div style="margin-bottom: 16px;">
            <label style="display: block; margin-bottom: 8px; font-size: 13px; font-weight: 500; color: var(--text-color);">
                Select State Machine:
            </label>
            <select id="sm-selector" style="
                width: 100%;
                padding: 10px 12px;
                border: 1px solid var(--input-border);
                border-radius: 8px;
                background: var(--bg-color);
                color: var(--text-color);
                font-size: 13px;
                cursor: pointer;
                outline: none;
            ">
                <option value="none">Select State Machine</option>
    `;

    stateMachines.forEach((sm) => {
        const isActive = window.currentStateMachineName === sm.name;
        html += `<option value="${sm.name}" ${isActive ? 'selected' : ''}>${sm.name}</option>`;
    });

    html += `
            </select>
        </div>
        <div id="sm-info" style="
            background: rgba(148, 163, 184, 0.1);
            border-radius: 8px;
            padding: 16px;
            margin-top: 16px;
        ">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                <div style="font-size: 12px; color: var(--text-color); opacity: 0.7;">
                    Current State Machine:
                </div>
                <button id="sm-reset-btn" style="
                    padding: 4px 12px;
                    border: 1px solid var(--input-border);
                    border-radius: 6px;
                    background: var(--button-bg);
                    color: var(--text-color);
                    cursor: pointer;
                    font-size: 11px;
                    font-weight: 600;
                    transition: all 0.2s;
                ">
                    🔄 Reset
                </button>
            </div>
            <div style="font-size: 14px; font-weight: 600; color: var(--tab-active); margin-bottom: 12px;">
                ${window.currentStateMachineName || 'None'}
            </div>
            <div id="sm-inputs-list"></div>
        </div>
    `;

    elements.debugStateMachinesContent.innerHTML = html;

    // Add change listener to select
    const selector = document.getElementById('sm-selector');
    if (selector) {
        selector.addEventListener('change', (e) => {
            const selectedSM = e.target.value;
            if (selectedSM !== 'none') {
                switchStateMachine(selectedSM);
            }
        });
    }

    // Add click listener to reset button
    const resetBtn = document.getElementById('sm-reset-btn');
    if (resetBtn) {
        resetBtn.addEventListener('click', () => {
            resetStateMachine(window.currentStateMachineName);
        });

        // Add hover effect
        resetBtn.addEventListener('mouseenter', () => {
            resetBtn.style.background = 'var(--button-hover)';
        });

        resetBtn.addEventListener('mouseleave', () => {
            resetBtn.style.background = 'var(--button-bg)';
        });
    }

    // Display current state machine inputs
    displayStateMachineInputs(instance, window.currentStateMachineName);
}

// Display state machine inputs
function displayStateMachineInputs(instance, stateMachineName) {
    const inputsList = document.getElementById('sm-inputs-list');
    if (!inputsList || !stateMachineName) return;

    // Store the current instance reference to validate later
    const currentInstance = instance;

    try {
        const inputs = instance.stateMachineInputs(stateMachineName);

        if (!inputs || inputs.length === 0) {
            inputsList.innerHTML = '<div style="font-size: 12px; color: #888;">No inputs available</div>';
            return;
        }

        let html = '<div style="font-size: 12px; font-weight: 600; color: var(--text-color); margin-bottom: 8px;">Inputs:</div>';

        inputs.forEach((input, index) => {
            const inputId = `sm-input-${index}`;
            const typeStr = input.type === rive.StateMachineInputType.Boolean ? 'Boolean' :
                           input.type === rive.StateMachineInputType.Number ? 'Number' :
                           input.type === rive.StateMachineInputType.Trigger ? 'Trigger' : 'Unknown';

            html += `
                <div style="
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 8px;
                    margin-bottom: 6px;
                    background: rgba(255, 255, 255, 0.05);
                    border-radius: 6px;
                    gap: 8px;
                ">
                    <div style="flex: 1; min-width: 0;">
                        <div style="color: var(--text-color); font-size: 12px; font-weight: 500; margin-bottom: 2px;">${input.name}</div>
                        <div style="color: #fbbf24; font-size: 10px; opacity: 0.8;">${typeStr}</div>
                    </div>
                    <div id="${inputId}" style="display: flex; align-items: center; gap: 8px;">
                        <!-- Control will be inserted here -->
                    </div>
                </div>
            `;
        });

        inputsList.innerHTML = html;

        // Add interactive controls for each input
        inputs.forEach((input, index) => {
            const inputId = `sm-input-${index}`;
            const container = document.getElementById(inputId);
            if (!container) return;

            if (input.type === rive.StateMachineInputType.Boolean) {
                // Boolean toggle
                const toggle = document.createElement('label');
                toggle.style.cssText = `
                    position: relative;
                    display: inline-block;
                    width: 40px;
                    height: 20px;
                    cursor: pointer;
                `;

                const checkbox = document.createElement('input');
                checkbox.type = 'checkbox';
                checkbox.checked = input.value;
                checkbox.style.cssText = 'opacity: 0; width: 0; height: 0;';

                const slider = document.createElement('span');
                slider.style.cssText = `
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: ${input.value ? '#22c55e' : '#ccc'};
                    transition: 0.3s;
                    border-radius: 20px;
                `;

                const knob = document.createElement('span');
                knob.style.cssText = `
                    position: absolute;
                    content: "";
                    height: 14px;
                    width: 14px;
                    left: ${input.value ? '23px' : '3px'};
                    bottom: 3px;
                    background-color: white;
                    transition: 0.3s;
                    border-radius: 50%;
                `;

                slider.appendChild(knob);
                toggle.appendChild(checkbox);
                toggle.appendChild(slider);

                checkbox.addEventListener('change', (e) => {
                    // Check if this instance is still valid
                    if (window.riveInstance !== currentInstance) {
                        console.warn('Instance changed, skipping update');
                        return;
                    }

                    try {
                        const newValue = e.target.checked;

                        // Re-fetch the current inputs to ensure they're valid
                        const currentInputs = window.riveInstance.stateMachineInputs(stateMachineName);
                        const currentInput = currentInputs.find(inp => inp.name === input.name);

                        if (currentInput) {
                            currentInput.value = newValue;
                            slider.style.backgroundColor = newValue ? '#22c55e' : '#ccc';
                            knob.style.left = newValue ? '23px' : '3px';
                            console.log(`Set ${currentInput.name} = ${newValue}`);
                        }
                    } catch (error) {
                        console.error('Error updating boolean input:', error);
                    }
                });

                container.appendChild(toggle);

            } else if (input.type === rive.StateMachineInputType.Number) {
                // Number input
                const numberInput = document.createElement('input');
                numberInput.type = 'number';
                numberInput.value = input.value !== undefined ? input.value : 0;
                numberInput.step = '0.1';
                numberInput.style.cssText = `
                    width: 70px;
                    padding: 4px 8px;
                    border: 1px solid var(--input-border);
                    border-radius: 4px;
                    background: var(--bg-color);
                    color: var(--text-color);
                    font-size: 11px;
                    text-align: right;
                `;

                numberInput.addEventListener('change', (e) => {
                    // Check if this instance is still valid
                    if (window.riveInstance !== currentInstance) {
                        console.warn('Instance changed, skipping update');
                        return;
                    }

                    try {
                        const newValue = parseFloat(e.target.value);
                        if (!isNaN(newValue)) {
                            // Re-fetch the current inputs to ensure they're valid
                            const currentInputs = window.riveInstance.stateMachineInputs(stateMachineName);
                            const currentInput = currentInputs.find(inp => inp.name === input.name);

                            if (currentInput) {
                                currentInput.value = newValue;
                                console.log(`Set ${currentInput.name} = ${newValue}`);
                            }
                        }
                    } catch (error) {
                        console.error('Error updating number input:', error);
                    }
                });

                // Add +/- buttons
                const btnGroup = document.createElement('div');
                btnGroup.style.cssText = 'display: flex; gap: 2px;';

                const minusBtn = document.createElement('button');
                minusBtn.textContent = '−';
                minusBtn.style.cssText = `
                    width: 22px;
                    height: 22px;
                    border: 1px solid var(--input-border);
                    border-radius: 4px;
                    background: var(--button-bg);
                    color: var(--text-color);
                    cursor: pointer;
                    font-size: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                `;
                minusBtn.addEventListener('click', () => {
                    // Check if this instance is still valid
                    if (window.riveInstance !== currentInstance) {
                        console.warn('Instance changed, skipping update');
                        return;
                    }

                    try {
                        const currentValue = parseFloat(numberInput.value) || 0;
                        const newValue = currentValue - 1;
                        numberInput.value = newValue;

                        // Re-fetch the current inputs to ensure they're valid
                        const currentInputs = window.riveInstance.stateMachineInputs(stateMachineName);
                        const currentInput = currentInputs.find(inp => inp.name === input.name);

                        if (currentInput) {
                            currentInput.value = newValue;
                            console.log(`Set ${currentInput.name} = ${newValue}`);
                        }
                    } catch (error) {
                        console.error('Error updating number input:', error);
                    }
                });

                const plusBtn = document.createElement('button');
                plusBtn.textContent = '+';
                plusBtn.style.cssText = minusBtn.style.cssText;
                plusBtn.addEventListener('click', () => {
                    // Check if this instance is still valid
                    if (window.riveInstance !== currentInstance) {
                        console.warn('Instance changed, skipping update');
                        return;
                    }

                    try {
                        const currentValue = parseFloat(numberInput.value) || 0;
                        const newValue = currentValue + 1;
                        numberInput.value = newValue;

                        // Re-fetch the current inputs to ensure they're valid
                        const currentInputs = window.riveInstance.stateMachineInputs(stateMachineName);
                        const currentInput = currentInputs.find(inp => inp.name === input.name);

                        if (currentInput) {
                            currentInput.value = newValue;
                            console.log(`Set ${currentInput.name} = ${newValue}`);
                        }
                    } catch (error) {
                        console.error('Error updating number input:', error);
                    }
                });

                btnGroup.appendChild(minusBtn);
                btnGroup.appendChild(numberInput);
                btnGroup.appendChild(plusBtn);
                container.appendChild(btnGroup);

            } else if (input.type === rive.StateMachineInputType.Trigger) {
                // Trigger button
                const triggerBtn = document.createElement('button');
                triggerBtn.textContent = 'Fire';
                triggerBtn.style.cssText = `
                    padding: 4px 12px;
                    border: none;
                    border-radius: 4px;
                    background: #2196F3;
                    color: white;
                    cursor: pointer;
                    font-size: 11px;
                    font-weight: 600;
                    transition: all 0.2s;
                `;

                triggerBtn.addEventListener('click', () => {
                    // Check if this instance is still valid
                    if (window.riveInstance !== currentInstance) {
                        console.warn('Instance changed, skipping update');
                        return;
                    }

                    try {
                        // Re-fetch the current inputs to ensure they're valid
                        const currentInputs = window.riveInstance.stateMachineInputs(stateMachineName);
                        const currentInput = currentInputs.find(inp => inp.name === input.name);

                        if (currentInput) {
                            currentInput.fire();
                            console.log(`Fired trigger: ${currentInput.name}`);

                            // Visual feedback
                            triggerBtn.style.background = '#1976D2';
                            setTimeout(() => {
                                triggerBtn.style.background = '#2196F3';
                            }, 150);
                        }
                    } catch (error) {
                        console.error('Error firing trigger:', error);
                    }
                });

                triggerBtn.addEventListener('mouseenter', () => {
                    triggerBtn.style.background = '#1976D2';
                });

                triggerBtn.addEventListener('mouseleave', () => {
                    triggerBtn.style.background = '#2196F3';
                });

                container.appendChild(triggerBtn);
            }
        });
    } catch (error) {
        console.error('Error displaying state machine inputs:', error);
        inputsList.innerHTML = '<div style="font-size: 12px; color: #ef4444;">Error loading inputs</div>';
    }
}

// Play animation
function playAnimation(animationName) {
    if (!window.riveInstance) {
        console.error('No Rive instance available');
        return;
    }

    try {
        // Stop current playback and switch to animation mode
        window.riveInstance.stop();

        // Play the specified animation
        window.riveInstance.play(animationName);

        console.log(`Playing animation: ${animationName}`);
    } catch (error) {
        console.error('Error playing animation:', error);
    }
}

// Switch state machine
function switchStateMachine(stateMachineName) {
    if (!window.riveInstance) {
        console.error('No Rive instance available');
        return;
    }

    try {
        console.log(`Switching to state machine: ${stateMachineName}`);

        // Store the current state machine name
        window.currentStateMachineName = stateMachineName;

        // Try to use the play method to switch state machine without reloading
        try {
            // Stop current playback
            window.riveInstance.stop();

            // Play the new state machine
            window.riveInstance.play(stateMachineName);

            console.log(`Successfully switched to state machine: ${stateMachineName}`);

            // Update controls for the new state machine
            handleStateMachineInputs(window.riveInstance, stateMachineName);

            // Update debug panel
            updateDebugStateMachines(window.riveInstance);
        } catch (playError) {
            // If play method fails, fall back to reloading
            console.warn('Play method failed, reloading instance:', playError);
            loadRiveFileWithStateMachine(config.sourceFile, [stateMachineName]);
        }
    } catch (error) {
        console.error('Error switching state machine:', error);
    }
}

// Reset state machine to initial state
function resetStateMachine(stateMachineName) {
    if (!window.riveInstance || !stateMachineName) {
        console.error('No Rive instance or state machine name available');
        return;
    }

    // Prevent multiple rapid resets
    if (window.isResetting) {
        console.log('Reset already in progress, skipping');
        return;
    }

    try {
        window.isResetting = true;
        console.log(`Resetting state machine: ${stateMachineName}`);

        // Try lightweight reset first: stop and replay
        try {
            // Stop current playback
            window.riveInstance.stop();

            // Small delay to ensure stop is processed
            setTimeout(() => {
                try {
                    // Replay the same state machine (this resets its state)
                    window.riveInstance.play(stateMachineName);

                    console.log(`Successfully reset state machine: ${stateMachineName}`);

                    // Update debug panel to refresh input values
                    updateDebugStateMachines(window.riveInstance);
                } catch (replayError) {
                    console.error('Error replaying state machine:', replayError);
                } finally {
                    window.isResetting = false;
                }
            }, 50);
        } catch (error) {
            console.error('Lightweight reset failed, trying full reload:', error);
            // If lightweight reset fails, fall back to full reload
            loadRiveFileWithStateMachine(config.sourceFile, [stateMachineName]);
            window.isResetting = false;
        }
    } catch (error) {
        console.error('Error resetting state machine:', error);
        window.isResetting = false;
    }
}

function updateTabContent(tabName) {
    switch (tabName) {
        case 'controls':
            updateControlsTab();
            break;
        case 'data-binding':
            updateDataBindingTab();
            break;
        case 'timeline':
            updateTimelineTab();
            break;
    }
}

function updateControlsTab() {
    // This will be populated by the existing control creation logic
    // The controls will be moved from the top panel to this tab
}

function updateDataBindingTab() {
    if (window.riveInstance) {
        handleDataBinding(window.riveInstance);
    }
}

function updateTimelineTab() {
    // Clear existing timeline content
    elements.timelineContent.innerHTML = '';

    if (window.riveInstance) {
        try {
            const defaultStateMachines = getStateMachinesFromName(config.sourceFile);

            // Check if timeline is available for this file
            let hasTimelineSchedule = true;

            // If no state machines, no timeline
            if (!defaultStateMachines || defaultStateMachines.length === 0) {
                hasTimelineSchedule = false;
            } else {
                try {
                    const testTimelineInput = window.riveInstance.stateMachineInputs(defaultStateMachines[0]);
                    if (!testTimelineInput || testTimelineInput.length === 0) {
                        hasTimelineSchedule = false;
                    } else {
                        // Special pattern check - we only support specific files in our timeline
                        if (!/ls-audio_animation/.test(config.sourceFile)) {
                            hasTimelineSchedule = false;
                        }
                    }
                } catch (error) {
                    console.warn('Cannot create timeline for this file:', error);
                    hasTimelineSchedule = false;
                }
            }
            
            if (!hasTimelineSchedule) {
                elements.timelineContent.innerHTML = `
                    <div style="text-align: center; padding: 40px 20px; color: #888;">
                        <div style="font-size: 48px; margin-bottom: 16px; opacity: 0.5;">⏱️</div>
                        <div style="font-size: 14px; font-weight: 500; margin-bottom: 8px;">No Timeline Available</div>
                        <div style="font-size: 12px; color: #666;">This animation doesn't support timeline controls</div>
                    </div>
                `;
                return;
            }
            
            // Create timeline controls section
            const timelineSection = document.createElement('div');
            timelineSection.className = 'control-section';
            
            const sectionTitle = document.createElement('div');
            sectionTitle.className = 'control-section-title';
            sectionTitle.textContent = 'Timeline Controls';
            timelineSection.appendChild(sectionTitle);
            
            // Create timeline controls container
            const timelineControlsContainer = document.createElement('div');
            timelineControlsContainer.className = 'control-buttons';
            timelineControlsContainer.style.justifyContent = 'center';
            
            // Create play/pause toggle button
            const toggleButton = document.createElement('button');
            toggleButton.className = 'trigger-btn';
            toggleButton.style.minWidth = '120px';
            toggleButton.style.display = 'flex';
            toggleButton.style.alignItems = 'center';
            toggleButton.style.justifyContent = 'center';
            toggleButton.style.gap = '8px';
            
            // Play icon (Unicode triangle)
            const playIcon = document.createElement('span');
            playIcon.textContent = '▶';
            
            // Pause icon (Unicode double bars)
            const pauseIcon = document.createElement('span');
            pauseIcon.textContent = '⏸';
            pauseIcon.style.display = 'none';
            
            // Add icons to button
            toggleButton.appendChild(playIcon);
            toggleButton.appendChild(pauseIcon);
            toggleButton.appendChild(document.createTextNode('Play'));
            
            // Status indicator
            const statusIndicator = document.createElement('div');
            statusIndicator.style.width = '12px';
            statusIndicator.style.height = '12px';
            statusIndicator.style.borderRadius = '50%';
            statusIndicator.style.backgroundColor = '#ccc';
            statusIndicator.style.marginLeft = '10px';
            statusIndicator.style.display = 'flex';
            statusIndicator.style.alignSelf = 'center';
            
            // Function to update button state
            const updateButtonState = (isPlaying) => {
                if (isPlaying) {
                    toggleButton.style.backgroundColor = '#f44336'; // Red when playing
                    playIcon.style.display = 'none';
                    pauseIcon.style.display = 'inline';
                    toggleButton.lastChild.nodeValue = 'Pause';
                    statusIndicator.style.backgroundColor = '#4CAF50';
                } else {
                    toggleButton.style.backgroundColor = '#2196F3'; // Blue when paused
                    playIcon.style.display = 'inline';
                    pauseIcon.style.display = 'none';
                    toggleButton.lastChild.nodeValue = 'Play';
                    statusIndicator.style.backgroundColor = '#ccc';
                }
            };
            
            // Check if timeline is already active
            const isActive = isTimelineActive(config.sourceFile, defaultStateMachines[0]);
            
            // For files with timeline support, start the timeline automatically
            if (!isActive) {
                try {
                    createTimeline(config.sourceFile, defaultStateMachines[0], window.riveInstance, rive);
                    console.log('Timeline started automatically');
                } catch (error) {
                    console.warn('Failed to start timeline automatically:', error);
                }
            }
            
            // Initialize button state
            updateButtonState(true);
            
            // Toggle click handler
            toggleButton.addEventListener('click', () => {
                const isActive = isTimelineActive(config.sourceFile, defaultStateMachines[0]);
                
                if (isActive) {
                    // If active, stop it
                    stopTimeline(config.sourceFile, defaultStateMachines[0]);
                    console.log('Timeline stopped manually');
                } else {
                    // If not active, start it
                    createTimeline(config.sourceFile, defaultStateMachines[0], window.riveInstance, rive);
                    console.log('Timeline started manually');
                }
                
                // Update button state
                updateButtonState(!isActive);
            });
            
            // Add elements to the timeline controls container
            timelineControlsContainer.appendChild(toggleButton);
            timelineControlsContainer.appendChild(statusIndicator);
            
            timelineSection.appendChild(timelineControlsContainer);
            elements.timelineContent.appendChild(timelineSection);
            
            // Add timeline info
            const infoSection = document.createElement('div');
            infoSection.className = 'control-section';
            infoSection.style.marginTop = '16px';
            
            const infoTitle = document.createElement('div');
            infoTitle.className = 'control-section-title';
            infoTitle.textContent = 'Timeline Info';
            infoSection.appendChild(infoTitle);
            
            const infoContent = document.createElement('div');
            infoContent.style.fontSize = '12px';
            infoContent.style.color = '#666';
            infoContent.style.lineHeight = '1.4';
            infoContent.innerHTML = `
                <p><strong>File:</strong> ${config.sourceFile}</p>
                <p><strong>State Machine:</strong> ${defaultStateMachines[0]}</p>
                <p><strong>Status:</strong> <span id="timeline-status">Active</span></p>
                <p><strong>Description:</strong> Automated animation sequence with position, rotation, and scale changes.</p>
            `;
            infoSection.appendChild(infoContent);
            
            elements.timelineContent.appendChild(infoSection);
            
        } catch (error) {
            console.error('Error creating timeline controls:', error);
            elements.timelineContent.innerHTML = `
                <div style="text-align: center; padding: 40px 20px; color: #ef4444;">
                    <div style="font-size: 48px; margin-bottom: 16px; opacity: 0.5;">⚠️</div>
                    <div style="font-size: 14px; font-weight: 500; margin-bottom: 8px;">Error Loading Timeline</div>
                    <div style="font-size: 12px; color: #fca5a5;">${error.message}</div>
                </div>
            `;
        }
    } else {
        elements.timelineContent.innerHTML = `
            <div style="text-align: center; padding: 40px 20px; color: #888;">
                <div style="font-size: 48px; margin-bottom: 16px; opacity: 0.5;">⏱️</div>
                <div style="font-size: 14px; font-weight: 500; margin-bottom: 8px;">No Animation Loaded</div>
                <div style="font-size: 12px; color: #666;">Load an animation to see timeline controls</div>
            </div>
        `;
    }
}

// Function to update canvas size
function updateCanvasSize() {
    elements.canvas.width = window.innerWidth;
    elements.canvas.height = window.innerHeight;
    
    // If riveInstance exists, resize its drawing surface
    if (window.riveInstance) {
        window.riveInstance.resizeDrawingSurfaceToCanvas();
    }
}

// Function to create buttons for Boolean and Trigger inputs
function createControlButtons(instance, stateMachineName) {
    // Clear existing controls
    elements.controlsContent.innerHTML = '';

    // If no state machine name provided, show message and return
    if (!stateMachineName) {
        elements.controlsContent.innerHTML = '<div style="color: #888; text-align: center; padding: 40px;">No state machine loaded. Animation is playing without interactive controls.</div>';
        return;
    }

    const HINT_REGEX = /^HINT_(.*)\/(.*)$/;

    try {
        const inputs = instance.stateMachineInputs(stateMachineName);
        console.log('readling '+inputs.length+' inputs from', stateMachineName);

        if (!inputs || inputs.length === 0) {
            console.error(`No inputs found for state machine "${stateMachineName}"`);
            elements.controlsContent.innerHTML = '<p>No controls available for this animation.</p>';
            return;
        }

        // Group inputs by type
        const booleanInputs = inputs.filter(input => input.type === rive.StateMachineInputType.Boolean);
        const triggerInputs = inputs.filter(input => input.type === rive.StateMachineInputType.Trigger)
        const numberInputs = inputs.filter(input => input.type === rive.StateMachineInputType.Number)
        
        // Add Theme Toggle button
        const themeToggleButton = document.createElement('button');
        themeToggleButton.className = 'theme-toggle-btn';
        themeToggleButton.style.marginBottom = '15px';
        themeToggleButton.style.padding = '8px 16px';
        themeToggleButton.style.width = 'auto';
        themeToggleButton.style.alignSelf = 'flex-start';
        themeToggleButton.style.fontWeight = 'bold';
        themeToggleButton.style.borderRadius = '6px';
        themeToggleButton.style.cursor = 'pointer';
        
        // Get current theme or default to light
        const currentTheme = document.documentElement.getAttribute('data-theme') || 'light';
        
        // Function to update theme toggle button text and icon
        function updateThemeButton(theme) {
            const icon = theme === 'dark' ? '☀️' : '🌙';
            const text = theme === 'dark' ? 'Light Mode' : 'Dark Mode';
            themeToggleButton.innerHTML = `<span>${icon}</span><span>${text}</span>`;
        }
        
        // Initialize button text
        updateThemeButton(currentTheme);
        
        // Theme toggle functionality
        themeToggleButton.addEventListener('click', () => {
            const currentTheme = document.documentElement.getAttribute('data-theme') || 'light';
            const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
            
            // Apply theme to document
            if (newTheme === 'dark') {
                document.documentElement.setAttribute('data-theme', 'dark');
            } else {
                document.documentElement.removeAttribute('data-theme');
            }
            
            // Update button
            updateThemeButton(newTheme);
            
            // Save preference to localStorage
            localStorage.setItem('theme-preference', newTheme);
            
            console.log(`Theme switched to ${newTheme} mode`);
        });
        
        elements.controlsContent.appendChild(themeToggleButton);
        
        // Create Boolean toggle buttons
        if (booleanInputs.length > 0) {
            const booleanSection = document.createElement('div');
            booleanSection.className = 'control-section';
            
            const sectionTitle = document.createElement('div');
            sectionTitle.className = 'control-section-title';
            sectionTitle.textContent = 'Boolean Controls';
            booleanSection.appendChild(sectionTitle);
            
            const buttonsContainer = document.createElement('div');
            buttonsContainer.className = 'control-buttons';
            
            booleanInputs.forEach(input => {
                const button = document.createElement('button');
                button.className = `toggle-btn ${input.value ? 'active' : ''}`;
                button.textContent = input.name.startsWith('HINT_') ? input.name.substring(5) : input.name;
                button.dataset.inputName = input.name;
                
                button.addEventListener('click', () => {
                    // Toggle the boolean value
                    let newValue = !input.value;
                    input.value = newValue;

                    if (HINT_REGEX.test(input.name)) {
                        const group = HINT_REGEX.exec(input.name);
                        const pathInput = instance.retrieveInputAtPath(group[2],group[1]);
                        // console.log('retrieved input at path', group[2], group[1], pathInput, pathInput.value);
                        pathInput.value = newValue;
                        instance.setBooleanStateAtPath(group[2], newValue, group[1]);
                        // console.log(`Toggled ${input.name} to ${newValue} via ${group[1]}/${group[2]}`);
                    }
                    
                    // Update button appearance
                    if (newValue) {
                        button.classList.add('active');
                    } else {
                        button.classList.remove('active');
                    }
                    
                    console.log(`Toggled ${input.name} to ${newValue}`);
                });
                
                buttonsContainer.appendChild(button);
            });
            
            booleanSection.appendChild(buttonsContainer);
            elements.controlsContent.appendChild(booleanSection);
        }
        
        // Create Number input controls
        if (numberInputs.length > 0) {
            const numberSection = document.createElement('div');
            numberSection.className = 'control-section';
            
            const sectionTitle = document.createElement('div');
            sectionTitle.className = 'control-section-title';
            sectionTitle.textContent = 'Number Controls';
            numberSection.appendChild(sectionTitle);
            
            const controlsContainer = document.createElement('div');
            controlsContainer.className = 'control-buttons';
            
            numberInputs.forEach(input => {
                // Create number control container
                const controlContainer = document.createElement('div');
                controlContainer.className = 'number-control';
                controlContainer.style.display = 'flex';
                controlContainer.style.flexDirection = 'column';
                controlContainer.style.gap = '5px';
                
                // Add label
                const label = document.createElement('div');
                label.className = 'number-label';
                label.textContent = input.name.startsWith('HINT_') ? input.name.substring(5) : input.name;
                controlContainer.appendChild(label);
                
                // Create input container
                const inputContainer = document.createElement('div');
                inputContainer.className = 'number-input-container';
                
                // Create number input
                const numberInput = document.createElement('input');
                numberInput.type = 'text';
                numberInput.className = 'number-input';
                numberInput.value = input.value !== undefined ? input.value : 0;
                
                // Function to update the input value
                function updateNumberValue(newValue) {
                    // Ensure newValue is a number
                    newValue = isNaN(newValue) ? 0 : newValue;
                    
                    // Update the input value
                    input.value = newValue;
                    numberInput.value = newValue;

                    if (HINT_REGEX.test(input.name)) {
                        const group = HINT_REGEX.exec(input.name);
                        const pathInput = instance.retrieveInputAtPath(group[2],group[1]);
                        // console.log('retrieved input at path', group[2], group[1], pathInput, pathInput.value);
                        pathInput.value = newValue;
                        instance.setNumberStateAtPath(group[2], newValue, group[1]);
                        // console.log(`Set Number ${input.name} to ${newValue} via ${group[1]}/${group[2]}`);
                    }
                    
                    console.log(`Changed ${input.name} to ${newValue}`);
                }
                
                // Create a container for the quick input buttons
                const quickButtonsContainer = document.createElement('div');
                quickButtonsContainer.style.display = 'flex';
                quickButtonsContainer.style.flexWrap = 'wrap';
                quickButtonsContainer.style.gap = '4px';
                
                // Special case for visemes-mouth input
                if (input.name === 'visemes-mouth') {
                    // Define viseme values
                    const visemeValues = [1, 2, 3, 100, 101, 102, 301, 302, 303, 304, 305, 307, 308, 309, 310, 312, 313, 314, 315, 316, 318];
                    
                    // Create quick input buttons for viseme values
                    visemeValues.forEach(value => {
                        const quickBtn = document.createElement('button');
                        quickBtn.className = 'trigger-btn';
                        quickBtn.textContent = value.toString();
                        quickBtn.style.padding = '3px 8px';
                        quickBtn.style.fontSize = '11px';
                        
                        quickBtn.addEventListener('click', () => {
                            updateNumberValue(value);
                        });
                        
                        quickButtonsContainer.appendChild(quickBtn);
                    });
                    
                    // Add only the quick buttons container
                    controlContainer.appendChild(quickButtonsContainer);
                } 
                // Special case for rotate input
                else if (input.name === 'rotate') {
                    // Define rotation values
                    const rotationValues = [-15, 0, 15];
                    
                    // Create quick input buttons for rotation values
                    rotationValues.forEach(value => {
                        const quickBtn = document.createElement('button');
                        quickBtn.className = 'trigger-btn';
                        quickBtn.textContent = value.toString() + '°';
                        quickBtn.style.padding = '3px 8px';
                        quickBtn.style.fontSize = '11px';
                        
                        quickBtn.addEventListener('click', () => {
                            updateNumberValue(value);
                        });
                        
                        quickButtonsContainer.appendChild(quickBtn);
                    });
                    
                    // Add only the quick buttons container
                    controlContainer.appendChild(quickButtonsContainer);
                }
                // Standard case for other number inputs
                else {
                    // Standard +/- controls for other number inputs
                    const minusBtn = document.createElement('div');
                    minusBtn.className = 'number-btn';
                    minusBtn.textContent = '-';
                    minusBtn.addEventListener('click', () => {
                        const currentValue = parseFloat(numberInput.value);
                        updateNumberValue(currentValue - 1);
                    });
                    inputContainer.appendChild(minusBtn);
                    
                    // Event listener for direct input
                    numberInput.addEventListener('change', () => {
                        const newValue = parseFloat(numberInput.value);
                        if (!isNaN(newValue)) {
                            updateNumberValue(newValue);
                        } else {
                            numberInput.value = input.value;
                        }
                    });
                    
                    inputContainer.appendChild(numberInput);
                    
                    // Create plus button
                    const plusBtn = document.createElement('div');
                    plusBtn.className = 'number-btn';
                    plusBtn.textContent = '+';
                    plusBtn.addEventListener('click', () => {
                        const currentValue = parseFloat(numberInput.value);
                        updateNumberValue(currentValue + 1);
                    });
                    inputContainer.appendChild(plusBtn);
                    
                    // Add input container to control container
                    controlContainer.appendChild(inputContainer);
                    
                    // Only add quick buttons container if it has children
                    if (quickButtonsContainer.children.length > 0) {
                        controlContainer.appendChild(quickButtonsContainer);
                    }
                }
                
                // Add control container to controls container
                controlsContainer.appendChild(controlContainer);
            });
            
            numberSection.appendChild(controlsContainer);
            elements.controlsContent.appendChild(numberSection);
        }
        
        // Create Trigger buttons
        if (triggerInputs.length > 0) {
            const triggerSection = document.createElement('div');
            triggerSection.className = 'control-section';
            
            const sectionTitle = document.createElement('div');
            sectionTitle.className = 'control-section-title';
            sectionTitle.textContent = 'Trigger Controls';
            triggerSection.appendChild(sectionTitle);
            
            const buttonsContainer = document.createElement('div');
            buttonsContainer.className = 'control-buttons';
            
            triggerInputs.forEach(input => {
                const button = document.createElement('button');
                button.className = 'trigger-btn';
                button.textContent = input.name.startsWith('HINT_') ? input.name.substring(5) : input.name;
                button.dataset.inputName = input.name;
                
                button.addEventListener('click', () => {
                    // Fire the trigger
                    input.fire();

                    if (HINT_REGEX.test(input.name)) {
                        const group = HINT_REGEX.exec(input.name);
                        const pathInput = instance.retrieveInputAtPath(group[2],group[1]);
                        // console.log('retrieved input at path', group[2], group[1], pathInput, pathInput.value);
                        instance.fireStateAtPath(group[2], group[1]);
                        // console.log(`Fired trigger ${input.name} via ${group[1]}/${group[2]}`);
                    }
                    console.log(`Fired trigger: ${input.name}`);
                    
                    // Visual feedback (flash effect)
                    button.style.opacity = '0.7';
                    setTimeout(() => {
                        button.style.opacity = '1';
                    }, 150);
                });
                
                buttonsContainer.appendChild(button);
            });
            
            triggerSection.appendChild(buttonsContainer);
            elements.controlsContent.appendChild(triggerSection);
        }
        
        // If no relevant inputs, show message
        if (booleanInputs.length === 0 && triggerInputs.length === 0 && numberInputs.length === 0) {
            elements.controlsContent.innerHTML = '<p>No controls available for this animation.</p>';
        }
    } catch (error) {
        console.error('Error creating control buttons:', error);
        elements.controlsContent.innerHTML = '<p>Error loading controls.</p>';
    }
}

// Function to handle state machine inputs
function handleStateMachineInputs(instance, stateMachineName) {
    // If no state machine name provided, skip
    if (!stateMachineName) {
        console.log('No state machine provided, skipping input handling');
        return;
    }

    try {
        const inputs = instance.stateMachineInputs(stateMachineName);

        if (!inputs || inputs.length === 0) {
            console.log(`No inputs found for state machine "${stateMachineName}"`);
            return;
        }
        
        inputs.forEach(input => {
            // console.log(`Input name: ${input.name}, type: ${input.type}`);
            
            switch (input.type) {
                case rive.StateMachineInputType.Number:
                    console.log(`[Number] ${input.name} = ${input.value}`, input);
                    break;
                case rive.StateMachineInputType.Boolean:
                    console.log(`[Boolean] ${input.name} =: ${input.value}`, input);
                    break;
                case rive.StateMachineInputType.Trigger:
                    console.log(`[Trigger] ${input.name}, can be fired`, input);
                    break;
                default:
                    console.log(`Unknown input type: ${input.type}`, input);
            }
        });
        
        // Create buttons for Boolean and Trigger inputs
        createControlButtons(instance, stateMachineName);
    } catch (error) {
        console.error('Error accessing state machine inputs:', error);
    }
}

// Function to get the state machines from URL hash
function getStateMachinesFromName(fileName) {
    if (/ani-/.test(config.sourceFile)) {
        return ['state'];
    }
    if (/laufey/.test(config.sourceFile)) {
        return ['main'];
    }
    if (/ls-characters/.test(config.sourceFile)) {
        return ['All_Animation_1'];
    }
    if (/ls-male|ls-animal/.test(config.sourceFile)) {
        return ['All_Animation'];
    }
    if (/ls-lion/.test(config.sourceFile)) {
        return ['Animation'];
    }
    if (/mouth/.test(config.sourceFile)) {
        return ['Mouths_2'];
    }
    if (/mop_head/.test(config.sourceFile)) {
        return ['State Machine 1'];
    }
    if (/ls-audio_animation/.test(config.sourceFile)) {
        // return ['A_旋转+位移都有'];
        return ['B_旋转与位移分开'];
    }
    if (/botim4/.test(config.sourceFile)) {
        return ['state machine'];
    }
    // Don't return a default value - let the caller handle it
    return null;
}

// Store available state machines globally for select dropdown
window.availableStateMachines = [];

// Function to load Rive file with specified state machine
function loadRiveFileWithStateMachine(fileName, stateMachines) {
// Stop all existing timelines
stopAllTimelines();

// Update the configuration
config.sourceFile = fileName;

// Update the watermark
elements.watermark.textContent = `AniSpark | Source: ${config.sourceFile}`;

// Clear the controls panel
elements.controlsContent.innerHTML = '';

// Stop data binding updates
stopDataBindingUpdates();

// Remove data binding elements
const dataBindingDiv = document.getElementById('data-binding-display');
if (dataBindingDiv) {
    dataBindingDiv.remove();
}

const dataBindingToggle = document.getElementById('data-binding-toggle');
if (dataBindingToggle) {
    dataBindingToggle.remove();
}

// Remove the old instance if it exists
if (window.riveInstance) {
    try {
        // Try to cleanup the old instance
        window.riveInstance.cleanup();
    } catch (cleanupError) {
        // Ignore cleanup errors, they might happen if WebGL context is lost
        console.warn('Error during cleanup (ignored):', cleanupError);
    }
    delete window.riveInstance;
}

// Create a new Rive instance with proper Layout object
const riveConfig = {
    canvas: elements.canvas,
    autoplay: true,
    src: config.sourceFile,
    layout: new rive.Layout({
        fit: config.fit,
        alignment: config.alignment
    }),
    onLoad: buildHandleRiveLoad(stateMachines),
    onError: (err) => {
        console.error('Error loading Rive animation:', err);
        // Just log the error, don't alert or redirect
    }
};

// Only add stateMachines if we have them
if (stateMachines && stateMachines.length > 0) {
    riveConfig.stateMachines = stateMachines;
}

window.riveInstance = new rive.Rive(riveConfig);

// Add state change listener
window.riveInstance.on('statechange', (event) => {
    console.log('State changed to:', event.data);
});
}

// Function to load a new Rive file
function loadRiveFile(fileName) {
    // Stop all existing timelines
    stopAllTimelines();

    // Update the configuration
    config.sourceFile = fileName;

    // Update the watermark
    elements.watermark.textContent = `AniSpark | Source: ${config.sourceFile}`;

    // Clear the controls panel
    elements.controlsContent.innerHTML = '';

    // Stop data binding updates
    stopDataBindingUpdates();

    // Remove data binding elements
    const dataBindingDiv = document.getElementById('data-binding-display');
    if (dataBindingDiv) {
        dataBindingDiv.remove();
    }

    const dataBindingToggle = document.getElementById('data-binding-toggle');
    if (dataBindingToggle) {
        dataBindingToggle.remove();
    }

    // Remove the old instance if it exists
    if (window.riveInstance) {
        try {
            // Try to cleanup the old instance
            window.riveInstance.cleanup();
        } catch (cleanupError) {
            // Ignore cleanup errors, they might happen if WebGL context is lost
            console.warn('Error during cleanup (ignored):', cleanupError);
        }
        delete window.riveInstance;
    }

    const defaultStateMachines = getStateMachinesFromName(fileName);

    // Create a new Rive instance with proper Layout object
    // If we have a predefined state machine, use it; otherwise load without specifying
    const riveConfig = {
        canvas: elements.canvas,
        autoplay: true,
        src: config.sourceFile,
        layout: new rive.Layout({
            fit: config.fit,
            alignment: config.alignment
        }),
        onLoad: () => {
            console.log('Rive file loaded successfully!');
            console.log('riveInstance:', window.riveInstance);

            // Get all available state machines
            const artboard = window.riveInstance.artboard;
            let stateMachineCount = 0;
            if (artboard) {
                if (typeof artboard.stateMachineCount === 'function') {
                    stateMachineCount = artboard.stateMachineCount();
                } else {
                    stateMachineCount = artboard.stateMachineCount || 0;
                }
            }
            const availableStateMachines = [];

            for (let i = 0; i < stateMachineCount; i++) {
                const smName = artboard.stateMachineByIndex(i).name;
                availableStateMachines.push(smName);
            }

            console.log('Available state machines:', availableStateMachines);

            // If we have predefined state machines, check if they exist
            if (defaultStateMachines && defaultStateMachines.length > 0) {
                const requestedSM = defaultStateMachines[0];
                if (availableStateMachines.includes(requestedSM)) {
                    // Reload with the correct state machine
                    loadRiveFileWithStateMachine(fileName, defaultStateMachines);
                } else {
                    // Use first available state machine
                    console.warn(`Requested state machine "${requestedSM}" not found, using first available: ${availableStateMachines[0]}`);
                    loadRiveFileWithStateMachine(fileName, [availableStateMachines[0]]);
                }
            } else {
                // No predefined state machine, use first available
                if (availableStateMachines.length === 0) {
                    // No state machines, just use the current instance without state machine
                    console.log('No state machines found, using artboard without state machine');
                    handleRiveLoad([]);
                } else {
                    // Use first available state machine
                    console.log(`No predefined state machine, using first available: ${availableStateMachines[0]}`);
                    loadRiveFileWithStateMachine(fileName, [availableStateMachines[0]]);
                }
            }
        },
        onError: (err) => {
            console.error('Error loading Rive animation:', err);

            // Check if the error is about state machine not found
            if (err.message && err.message.includes('not found')) {
                // Try to reload without state machine to get available options
                console.log('State machine not found, reloading to get available options...');

                // Create a temporary instance to get state machines
                const tempRive = new rive.Rive({
                    canvas: elements.canvas,
                    src: config.sourceFile,
                    autoplay: false,
                    layout: new rive.Layout({
                        fit: config.fit,
                        alignment: config.alignment
                    }),
                    onLoad: () => {
                        const artboard = tempRive.artboard;
                        let stateMachineCount = 0;
                        if (artboard) {
                            if (typeof artboard.stateMachineCount === 'function') {
                                stateMachineCount = artboard.stateMachineCount();
                            } else {
                                stateMachineCount = artboard.stateMachineCount || 0;
                            }
                        }
                        const availableStateMachines = [];

                        for (let i = 0; i < stateMachineCount; i++) {
                            const smName = artboard.stateMachineByIndex(i).name;
                            availableStateMachines.push(smName);
                        }

                        console.log('Available state machines after error:', availableStateMachines);

                        // Cleanup temp instance
                        tempRive.cleanup();
                        delete window.riveInstance;

                        // Use first available state machine or none
                        if (availableStateMachines.length > 0) {
                            console.log(`Using first available state machine: ${availableStateMachines[0]}`);
                            loadRiveFileWithStateMachine(fileName, [availableStateMachines[0]]);
                        } else {
                            // No state machines, reload without state machine
                            console.log('No state machines found, loading without state machine');
                            loadRiveFileWithStateMachine(fileName, []);
                        }
                    },
                    onError: (tempErr) => {
                        console.error('Error loading Rive animation for state machine detection:', tempErr);
                        // Still an error even without state machine, show error message but stay on page
                        console.warn('Cannot load this Rive file:', tempErr.message);
                    }
                });

                window.riveInstance = tempRive;
            } else {
                // Other errors, just log and stay on page
                console.error('Error loading animation:', err.message);
            }
        }
    };

    // Only add stateMachines if we have predefined ones
    if (defaultStateMachines && defaultStateMachines.length > 0) {
        riveConfig.stateMachines = defaultStateMachines;
    }

    window.riveInstance = new rive.Rive(riveConfig);

    // Add state change listener
    window.riveInstance.on('statechange', (event) => {
        console.log('State changed to:', event.data);
    });
}

// Function to initialize Rive
function initRive() {
    // Initial setup of canvas size
    updateCanvasSize();
    
    // Load the Rive file from configuration
    loadRiveFile(config.sourceFile);
}

function buildHandleRiveLoad(defaultStateMachines) {
    return function () {
        handleRiveLoad(defaultStateMachines);
    }
}

// Function to handle Rive load event
function handleRiveLoad(defaultStateMachines) {
    try {
        console.log('Rive animation loaded successfully!');
        console.log(`Loaded file: ${config.sourceFile}`);

        // Resize the drawing surface to match the canvas size
        window.riveInstance.resizeDrawingSurfaceToCanvas();

        // Get information about the loaded Rive file
        console.log('Current artboard:', window.riveInstance.artboard);

        // Handle state machine inputs and create control buttons
        if (defaultStateMachines && defaultStateMachines.length > 0) {
            handleStateMachineInputs(window.riveInstance, defaultStateMachines[0]);
        } else {
            // No state machine, just show a message in controls
            elements.controlsContent.innerHTML = '<div style="color: #888; text-align: center; padding: 40px;">No state machine loaded. Animation is playing without interactive controls.</div>';
        }

        // Initialize data binding functionality
        handleDataBinding(window.riveInstance);
        startDataBindingUpdates();
        
        // Update timeline tab if it's currently active
        const activeTab = document.querySelector('.tab-button.active');
        if (activeTab && activeTab.getAttribute('data-tab') === 'timeline') {
            updateTimelineTab();
        }

        // Update debug panel with animations and state machines
        updateDebugAnimations(window.riveInstance);
        updateDebugStateMachines(window.riveInstance);

        // Store current state machine name if available
        if (defaultStateMachines && defaultStateMachines.length > 0) {
            window.currentStateMachineName = defaultStateMachines[0];
        }
    } catch (error) {
        console.error('Error during Rive initialization:', error);
    }
}

// Function to handle data binding and display values in the bottom sheet
function handleDataBinding(instance) {
    try {
        // Get all view models from the Rive instance
        const viewModelCount = instance.viewModelCount || 0;
        
        if (viewModelCount === 0) {
            // console.log('No view models available for data binding');
            elements.dataBindingContent.innerHTML = '<div style="color: #888; text-align: center; padding: 40px;">No view models available</div>';
            return;
        }

        // Function to iterate through all view models and their properties
        function iterateAllViewModels() {
            if (window.allProperties)
                return window.allProperties;

            const allProperties = [];
            
            for (let i = 0; i < viewModelCount; i++) {
                try {
                    const viewModel = instance.viewModelByIndex(i);
                    if (viewModel) {
                        console.log(`Processing view model ${i}:`, viewModel);
                        
                        // Get the view model instance
                        const vmInstance = viewModel.instanceByIndex(0);
                        instance.bindViewModelInstance(vmInstance);
                        
                        // Get properties from this view model
                        const properties = iterateDataBindingProperties(viewModel, vmInstance, `${viewModel.name}`);
                        allProperties.push(...properties);
                    }
                } catch (error) {
                    console.error(`Error processing view model ${i}:`, error);
                }
            }
            
            window.allProperties = allProperties;
            return allProperties;
        }
        
        // Function to recursively iterate through data binding properties
        function iterateDataBindingProperties(viewModel, instance, path = '') {
            const properties = [];
            
            try {
                // Get all properties from the view model instance
                const allProperties = viewModel.properties ? viewModel.properties : [];
                console.log(`Properties for ${path}:`, allProperties);
                
                // Process each property based on its type
                allProperties.forEach(prop => {
                    console.log('prop', prop);
                    try {
                        let value = null;
                        
                        switch (prop.type) {
                            case 'number':
                                const numProp = instance.number(prop.name);
                                if (numProp) {
                                    value = numProp;
                                } else {
                                    console.log('Error numProp', numProp);
                                }
                                break;
                            default:
                                console.log(`Unknown property type: ${prop.type} for property: ${prop.name}`);
                                break;
                        }
                        
                        if (value) {
                            properties.push({
                                name: prop.name,
                                type: prop.type,
                                value: value,
                                path: path ? `${path}.${prop.name}` : prop.name
                            });
                        }
                    } catch (error) {
                        // Skip properties that can't be accessed
                        console.log('error', error);
                    }
                });
                
            } catch (error) {
                console.error('Error iterating data binding properties:', error);
            }
            
            return properties;
        }

        // Get all data binding properties from all view models
        const dataBindingProperties = iterateAllViewModels();
        
        // Update the bottom sheet content
        if (dataBindingProperties.length > 0) {
            let html = `
                <div class="data-binding-header">
                    <div class="data-binding-title">
                        <span>🔗</span>
                        <span>${config.sourceFile}</span>
                        <span class="data-binding-count">${dataBindingProperties.length}</span>
                    </div>
                </div>
            `;
            
            // Group properties by type
            const groupedProperties = {};
            dataBindingProperties.forEach(prop => {
                if (!groupedProperties[prop.type]) {
                    groupedProperties[prop.type] = [];
                }
                groupedProperties[prop.type].push(prop);
            });
            
            // Display properties grouped by type
            Object.entries(groupedProperties).forEach(([type, props]) => {
                html += `<div class="property-group">`;
                html += `<div class="property-group-title">
                    <span style="width: 8px; height: 8px; background: #fbbf24; border-radius: 50%;"></span>
                    ${type}
                </div>`;
                
                props.forEach(prop => {
                    const pathDisplay = prop.path.length > 25 ? `...${prop.path.slice(-22)}` : prop.path;
                    let valueDisplay = prop.value.value;
                    if (prop.type == 'number') {
                        valueDisplay = prop.value.value.toFixed(2);
                    }
                    html += `<div class="property-item">`;
                    html += `<span class="property-name">${pathDisplay}</span>`;
                    html += `<span class="property-value">${valueDisplay}</span>`;
                    html += `</div>`;
                });
                
                html += `</div>`;
            });
            
            elements.dataBindingContent.innerHTML = html;
        } else {
            elements.dataBindingContent.innerHTML = `
                <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 40px 20px; text-align: center;">
                    <div style="font-size: 48px; margin-bottom: 16px; opacity: 0.5;">🔗</div>
                    <div style="color: #94a3b8; font-size: 14px; font-weight: 500;">No data binding properties found</div>
                    <div style="color: #64748b; font-size: 12px; margin-top: 4px;">This Rive file doesn't contain any bindable properties</div>
                </div>
            `;
        }
        
    } catch (error) {
        console.error('Error handling data binding:', error);
        
        // Show error in the bottom sheet
        elements.dataBindingContent.innerHTML = `
            <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 40px 20px; text-align: center;">
                <div style="font-size: 48px; margin-bottom: 16px; opacity: 0.5;">⚠️</div>
                <div style="color: #ef4444; font-size: 14px; font-weight: 600; margin-bottom: 8px;">Error Loading Data</div>
                <div style="color: #fca5a5; font-size: 12px; background: rgba(239, 68, 68, 0.1); padding: 8px 12px; border-radius: 8px; border: 1px solid rgba(239, 68, 68, 0.2);">${error.message}</div>
            </div>
        `;
    }
}

// Function to start periodic updates of data binding values
function startDataBindingUpdates() {
    // Clear any existing interval
    if (window.dataBindingUpdateInterval) {
        clearInterval(window.dataBindingUpdateInterval);
    }
    
    // Start periodic updates every 100ms
    window.dataBindingUpdateInterval = setInterval(() => {
        const activeTab = document.querySelector('.tab-button.active');
        if (activeTab && activeTab.getAttribute('data-tab') === 'data-binding' && window.riveInstance) {
            handleDataBinding(window.riveInstance);
        }
    }, 100);
}

// Function to stop periodic updates
function stopDataBindingUpdates() {
    if (window.dataBindingUpdateInterval) {
        clearInterval(window.dataBindingUpdateInterval);
        window.dataBindingUpdateInterval = null;
    }
}

// Handle hash change events to load different Rive files
function handleHashChange() {
    const newFileName = getRiveFileFromHash();
    if (newFileName !== config.sourceFile) {
        loadRiveFile(newFileName);
    }
}

// Initialize the application
function init() {
    // Load saved theme preference
    const savedTheme = localStorage.getItem('theme-preference');
    if (savedTheme === 'dark') {
        document.documentElement.setAttribute('data-theme', 'dark');
    } else {
        document.documentElement.removeAttribute('data-theme');
    }
    
    // Initialize bottom sheet
    initBottomSheet();

    // Initialize debug panel
    initDebugPanel();

    // Add event listeners
    window.addEventListener('resize', updateCanvasSize);
    window.addEventListener('hashchange', handleHashChange);

    // Initialize Rive
    initRive();
}

// Make timeline functions globally accessible for testing
window.createTimeline = createTimeline;
window.stopTimeline = stopTimeline;
window.stopAllTimelines = stopAllTimelines;

// Start the application when the DOM is loaded
document.addEventListener('DOMContentLoaded', init); 
